import React, { useState, useMemo } from 'react';
import { type TokenUsageLog, formatMoney, formatSignedMoney, formatNetMoney } from '../../../lib/apis_vibe';

interface SchoolLogsTabProps {
  logs: TokenUsageLog[];
  loading: boolean;
}

const SchoolLogsTab: React.FC<SchoolLogsTabProps> = ({ logs, loading }) => {
  // 筛选状态内部管理
  const [filters, setFilters] = useState({
    dateStart: '',
    dateEnd: '',
    type: 'all', // all, recharge, consume
    keyword: ''  // ✅ 修改：从 studentId 改为 keyword，用于通用搜索
  });

  // 过滤逻辑
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      // 1. 类型过滤
      if (filters.type === 'recharge' && log.amount > 0) return false;
      if (filters.type === 'consume' && log.amount < 0) return false;
      
      // 2. ✅ 修改：学员搜索 (ID 或 姓名)
      if (filters.keyword) {
        const term = filters.keyword.toLowerCase();
        
        // 获取 ID 字符串 (防御性处理)
        const idStr = log.user_id ? log.user_id.toString() : '';
        
        // 获取 用户名 (假设接口已按上一条建议更新，包含了 user 对象)
        // 使用 (log as any) 临时规避类型检查，防止前端接口定义未及时更新导致报错
        const userName = (log as any).user?.username || '';
        
        const idMatch = idStr.includes(term);
        const nameMatch = userName.toLowerCase().includes(term);

        if (!idMatch && !nameMatch) {
          return false;
        }
      }

      // 3. 时间过滤
      const time = new Date(log.created_at).getTime();
      if (filters.dateStart && time < new Date(filters.dateStart).getTime()) return false;
      if (filters.dateEnd) {
        const end = new Date(filters.dateEnd);
        end.setHours(23, 59, 59);
        if (time > end.getTime()) return false;
      }
      return true;
    });
  }, [logs, filters]);

  // 统计逻辑
  const stats = useMemo(() => {
    let inc = 0, dec = 0;
    let incMoney = 0, decMoney = 0;
    filteredLogs.forEach(l => {
      const tm = Number(l.total_money) || 0;
      if (l.amount < 0) {
        inc += Math.abs(l.amount);
        incMoney += Math.abs(tm);
      } else {
        dec += l.amount;
        decMoney += tm;
      }
    });
    return { inc, dec, net: inc - dec, incMoney, decMoney, netMoney: incMoney - decMoney };
  }, [filteredLogs]);

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      {/* 筛选面板 */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">开始日期</label>
            <input type="date" className="w-full border p-2 rounded text-sm outline-none focus:ring-2 focus:ring-blue-500" value={filters.dateStart} onChange={e => setFilters({...filters, dateStart: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">结束日期</label>
            <input type="date" className="w-full border p-2 rounded text-sm outline-none focus:ring-2 focus:ring-blue-500" value={filters.dateEnd} onChange={e => setFilters({...filters, dateEnd: e.target.value})} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">交易类型</label>
            <select className="w-full border p-2 rounded text-sm outline-none focus:ring-2 focus:ring-blue-500" value={filters.type} onChange={e => setFilters({...filters, type: e.target.value})}>
              <option value="all">全部类型</option>
              <option value="recharge">仅充值 (收入)</option>
              <option value="consume">仅消耗 (支出)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">学员搜索</label>
            {/* ✅ 修改：输入框支持 ID 或 姓名 */}
            <input 
              type="text" 
              placeholder="搜索学员ID或姓名..." 
              className="w-full border p-2 rounded text-sm outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.keyword}
              onChange={e => setFilters({...filters, keyword: e.target.value})}
            />
          </div>
        </div>
        
        {/* 统计卡片 */}
        <div className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
          <div className="flex-1">
            <div className="text-xs text-green-600 mb-1 font-medium">区间总充值</div>
            <div className="text-xl font-bold text-green-700">+{stats.inc.toLocaleString()}</div>
            <div className="text-xs text-green-500 mt-0.5">+{formatMoney(stats.incMoney)}</div>
          </div>
          <div className="flex-1 border-l border-gray-200 pl-4">
            <div className="text-xs text-red-600 mb-1 font-medium">区间总消耗</div>
            <div className="text-xl font-bold text-red-700">-{stats.dec.toLocaleString()}</div>
            <div className="text-xs text-red-500 mt-0.5">-{formatMoney(stats.decMoney)}</div>
          </div>
          <div className="flex-1 border-l border-gray-200 pl-4">
            <div className="text-xs text-gray-600 mb-1 font-medium">区间净增</div>
            <div className={`text-xl font-bold ${stats.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.net > 0 ? '+' : ''}{stats.net.toLocaleString()}
            </div>
            <div className={`text-xs mt-0.5 ${stats.netMoney >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {formatNetMoney(stats.netMoney)}
            </div>
          </div>
        </div>
      </div>

      {/* 日志列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="max-h-[600px] overflow-y-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-600 font-semibold border-b sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3">时间</th>
                <th className="px-6 py-3">学员信息</th> {/* ✅ 修改表头 */}
                <th className="px-6 py-3">类型</th>
                <th className="px-6 py-3 text-right">变动数量</th>
                <th className="px-6 py-3 pl-6">备注</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                 <tr><td colSpan={5} className="p-10 text-center text-gray-500">数据加载中...</td></tr>
              ) : filteredLogs.length === 0 ? (
                 <tr><td colSpan={5} className="p-10 text-center text-gray-400">无符合条件的记录</td></tr>
              ) : (
                filteredLogs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    
                    {/* ✅ 修改：显示姓名和ID */}
                    <td className="px-6 py-3">
                      <div className="font-medium text-gray-900">
                        {(log as any).user?.username || '未知学员'}
                      </div>
                      <div className="text-xs text-gray-400 font-mono">
                        ID: {log.user_id}
                      </div>
                      <div className="flex gap-2 mt-1">
                        <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{(log as any).user?.role || '学员'}</span>
                        {(log as any).user?.memo && <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded truncate max-w-[120px]" title={(log as any).user?.memo}>{(log as any).user?.memo}</span>}
                      </div>
                    </td>

                    <td className="px-6 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        log.related_type === 'chat_message' ? 'bg-purple-100 text-purple-700' :
                        log.related_type === 'text' ? 'bg-green-100 text-green-700' :
                        log.related_type === 'image' ? 'bg-blue-100 text-blue-700' :
                        log.related_type === 'video' ? 'bg-indigo-100 text-indigo-700' :
                        log.related_type === 'other' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {log.related_type === 'chat_message' ? 'AI对话' :
                         log.related_type === 'text' ? '文本生成' :
                         log.related_type === 'image' ? '图片生成' :
                         log.related_type === 'video' ? '视频生成' :
                         log.related_type === 'other' && log.amount < 0 ? '充值' :
                         log.related_type === 'other' ? '其他操作' :
                         '后台操作'}
                      </span>
                    </td>
                    <td className={`px-6 py-3 text-right font-bold ${log.amount < 0 ? 'text-green-600' : 'text-red-600'}`}>
                      <div>{log.amount < 0 ? `+${Math.abs(log.amount)}` : `-${log.amount}`}</div>
                      <div className="text-xs text-gray-400 font-normal mt-0.5">
                        {formatSignedMoney(log.total_money, log.amount < 0)}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-gray-500 truncate max-w-xs pl-6">
                      {log.remark || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SchoolLogsTab;
